import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Package, Copy, Search, Filter, Star, Settings, X, Check } from 'lucide-react';
import apiService from '../services/api';

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    categorie: '',
    options: [],
    contraintes: []
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const data = await apiService.request('/templates');
      setTemplates(data);
      setError('');
    } catch (err) {
      setError('Erreur lors du chargement des templates');
      console.error('Templates error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter(template =>
    searchTerm === '' ||
    template.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.categorie?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setFormData({
      nom: template.nom || '',
      description: template.description || '',
      categorie: template.categorie || '',
      options: template.options || [],
      contraintes: template.contraintes || []
    });
    setShowModal(true);
  };

  const handleNew = () => {
    setSelectedTemplate(null);
    setFormData({
      nom: '',
      description: '',
      categorie: '',
      options: [],
      contraintes: []
    });
    setShowModal(true);
  };

  const handleDuplicate = async (template) => {
    if (!confirm(`Dupliquer "${template.nom}" ?`)) return;
    
    try {
      const newTemplate = {
        ...template,
        nom: `${template.nom} (Copie)`,
        id: undefined,
        createdAt: undefined,
        updatedAt: undefined
      };
      
      await apiService.request('/templates', {
        method: 'POST',
        body: JSON.stringify(newTemplate)
      });
      
      fetchTemplates();
    } catch (error) {
      console.error('Erreur duplication:', error);
      alert('Erreur lors de la duplication');
    }
  };

  const handleDelete = async (template) => {
    if (!confirm(`Supprimer "${template.nom}" ?\nCette action est irréversible.`)) return;
    
    try {
      await apiService.request(`/templates/${template.id}`, {
        method: 'DELETE'
      });
      
      fetchTemplates();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, {
        id: `temp-${Date.now()}`,
        nom: '',
        obligatoire: false,
        choixMultiple: false,
        choix: []
      }]
    }));
  };

  const updateOption = (index, field, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    if (field === 'choixMultiple') {
      newOptions[index].type = value ? 'CHOIX_MULTIPLE' : 'CHOIX_UNIQUE';
    } else if (field === 'type') {
      newOptions[index].choixMultiple = value === 'CHOIX_MULTIPLE';
    }
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const removeOption = (index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const addChoixToOption = (optionIndex) => {
    const newOptions = [...formData.options];
    newOptions[optionIndex].choix.push({
      id: `temp-${Date.now()}`,
      nom: '',
      prix: 0
    });
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const updateChoix = (optionIndex, choixIndex, field, value) => {
    const newOptions = [...formData.options];
    newOptions[optionIndex].choix[choixIndex] = {
      ...newOptions[optionIndex].choix[choixIndex],
      [field]: value
    };
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const removeChoix = (optionIndex, choixIndex) => {
    const newOptions = [...formData.options];
    newOptions[optionIndex].choix = newOptions[optionIndex].choix.filter((_, i) => i !== choixIndex);
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const handleSubmit = async () => {
    if (!formData.nom) {
      alert('Le nom est obligatoire');
      return;
    }

    try {
      const dataToSave = {
        ...formData,
        options: formData.options.filter(opt => opt.nom && opt.choix.length > 0)
      };

      selectedTemplate
        ? await apiService.request(`/templates/${selectedTemplate.id}`, {
            method: 'PUT',
            body: JSON.stringify(dataToSave)
          })
        : await apiService.request('/templates', {
            method: 'POST',
            body: JSON.stringify(dataToSave)
          });

      fetchTemplates();
      setShowModal(false);
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
            <p className="text-gray-600">Gestion des templates de produits</p>
          </div>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">📋 Templates de Produits</h1>
            <p className="text-gray-600">Récapitulatif des templates disponibles sur la plateforme</p>
          </div>
          {/* Bouton retiré : les templates se créent lors de la création de produits */}
        </div>

        {/* Filtres et recherche */}
        <div className="card">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0 md:space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un template..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Stats rapides */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total templates</p>
                <p className="text-2xl font-bold text-gray-900">{templates.length}</p>
              </div>
              <Package className="h-10 w-10 text-primary-600 opacity-20" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avec options</p>
                <p className="text-2xl font-bold text-blue-600">
                  {templates.filter(t => t.options && t.options.length > 0).length}
                </p>
              </div>
              <Settings className="h-10 w-10 text-blue-600 opacity-20" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avec contraintes</p>
                <p className="text-2xl font-bold text-orange-600">
                  {templates.filter(t => t.contraintes && t.contraintes.length > 0).length}
                </p>
              </div>
              <Star className="h-10 w-10 text-orange-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* Liste des templates */}
        {filteredTemplates.length === 0 ? (
          <div className="card text-center py-12">
            <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'Aucun template trouvé' : 'Aucun template'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm 
                ? 'Essayez de modifier votre recherche'
                : 'Les templates se créent automatiquement lors de la création de produits'}
            </p>
            {/* Bouton retiré : les templates se créent lors de la création de produits */}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {template.nom}
                      </h3>
                      {template.categorie && (
                        <p className="text-xs text-gray-500 mb-2">
                          {template.categorie}
                        </p>
                      )}
                      {template.description && (
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {template.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    {template.options && template.options.length > 0 && (
                      <div className="flex items-center text-sm text-blue-600">
                        <Settings className="h-4 w-4 mr-1" />
                        <span>{template.options.length} option{template.options.length > 1 ? 's' : ''}</span>
                      </div>
                    )}
                    
                    {template.contraintes && template.contraintes.length > 0 && (
                      <div className="flex items-center text-sm text-orange-600">
                        <Star className="h-4 w-4 mr-1" />
                        <span>{template.contraintes.length} contrainte{template.contraintes.length > 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions retirées : les templates se gèrent lors de la création de produits */}
                  <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                    📋 Template disponible pour la création de produits
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info bulle */}
        {filteredTemplates.length > 0 && (
          <div className="card bg-blue-50 border border-blue-200">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <span className="text-2xl">💡</span>
              </div>
              <div>
                <h4 className="font-medium text-blue-900 mb-1">Templates réutilisables</h4>
                <p className="text-sm text-blue-700">
                  Créez des templates pour standardiser vos produits • Dupliquez et modifiez facilement • 
                  Appliquez les templates lors de la création de nouveaux produits
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal retirée : les templates se créent lors de la création de produits */}
      {false && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)}></div>
            
            <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
              <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-700">
                <h3 className="text-xl font-semibold text-white">
                  {selectedTemplate ? '✏️ Modifier le template' : '➕ Nouveau template'}
                </h3>
                <button onClick={() => setShowModal(false)} className="p-2 text-white hover:bg-white/20 rounded-lg transition">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="space-y-6">
                  {/* Informations de base */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom du template *
                      </label>
                      <input
                        type="text"
                        value={formData.nom}
                        onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Ex: Template Pizza Classique"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Catégorie
                      </label>
                      <input
                        type="text"
                        value={formData.categorie}
                        onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Ex: Pizza, Sushi"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Description du template..."
                      />
                    </div>
                  </div>

                  {/* Options */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Options & Variantes
                      </label>
                      <button
                        onClick={addOption}
                        className="flex items-center space-x-1 text-sm text-primary-600 hover:text-primary-700"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Ajouter une option</span>
                      </button>
                    </div>

                    {formData.options.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                        <p className="text-sm text-gray-500">Aucune option définie</p>
                        <button
                          onClick={addOption}
                          className="mt-3 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm"
                        >
                          ➕ Créer ma première option
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {formData.options.map((option, optIndex) => (
                          <div key={option.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1 grid grid-cols-2 gap-3">
                                <input
                                  type="text"
                                  value={option.nom}
                                  onChange={(e) => updateOption(optIndex, 'nom', e.target.value)}
                                  placeholder="Nom de l'option (ex: Taille, Sauce...)"
                                  className="px-3 py-2 border border-gray-300 rounded-lg bg-white"
                                />
                                <div className="flex items-center space-x-4">
                                  <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={option.obligatoire}
                                      onChange={(e) => updateOption(optIndex, 'obligatoire', e.target.checked)}
                                      className="w-4 h-4 text-primary-600"
                                    />
                                    <span className="text-sm text-gray-700">Obligatoire</span>
                                  </label>
                                  <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={option.choixMultiple}
                                      onChange={(e) => updateOption(optIndex, 'choixMultiple', e.target.checked)}
                                      className="w-4 h-4 text-primary-600"
                                    />
                                    <span className="text-sm text-gray-700">Choix multiple</span>
                                  </label>
                                </div>
                              </div>
                              <button
                                onClick={() => removeOption(optIndex)}
                                className="ml-2 p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>

                            {/* Choix de l'option */}
                            <div className="ml-4 space-y-2">
                              {option.choix.map((choix, choixIndex) => (
                                <div key={choix.id} className="flex items-center space-x-2">
                                  <input
                                    type="text"
                                    value={choix.nom}
                                    onChange={(e) => updateChoix(optIndex, choixIndex, 'nom', e.target.value)}
                                    placeholder="Nom du choix (ex: Petit, Grand...)"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
                                  />
                                  <div className="flex items-center space-x-1">
                                    <span className="text-sm text-gray-500">+</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={choix.prix}
                                      onChange={(e) => updateChoix(optIndex, choixIndex, 'prix', parseFloat(e.target.value) || 0)}
                                      className="w-20 px-2 py-2 border border-gray-300 rounded-lg bg-white text-sm"
                                      placeholder="0.00"
                                    />
                                    <span className="text-sm text-gray-500">€</span>
                                  </div>
                                  <button
                                    onClick={() => removeChoix(optIndex, choixIndex)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                              <button
                                onClick={() => addChoixToOption(optIndex)}
                                className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                              >
                                <Plus className="h-4 w-4" />
                                <span>Ajouter un choix</span>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!formData.nom}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition flex items-center space-x-2"
                >
                  <Check className="h-4 w-4" />
                  <span>💾 Enregistrer</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Templates;


